import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../App";
import { useSettings } from "../contexts/SettingsContext";
import { login as loginApi, requestPasswordReset, getSystemStatus, register, getPublicSchools } from "../lib/api";
import api from "../lib/api";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [setupName, setSetupName] = useState("");
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [loginRole, setLoginRole] = useState("staff");
  const [studentCode, setStudentCode] = useState("");
  const [studentSchools, setStudentSchools] = useState([]);
  const [studentSchoolId, setStudentSchoolId] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();
  const { refreshFromBackend } = useSettings();

  useEffect(() => {
    let settled = false;
    // Safety net: never hang on "Loading..." — show the login form after 8s regardless.
    const fallback = setTimeout(() => { if (!settled) setCheckingSystem(false); }, 8000);
    const checkSystem = async () => {
      try {
        const res = await getSystemStatus();
        if (!res.data.has_users) setMode("setup");
      } catch { /* default to login */ } finally {
        settled = true;
        clearTimeout(fallback);
        setCheckingSystem(false);
      }
    };
    checkSystem();
    return () => clearTimeout(fallback);
  }, []);

  // Load public school directory when the student tab is active (for the school picker)
  useEffect(() => {
    if (loginRole === "student" && studentSchools.length === 0) {
      getPublicSchools().then((res) => setStudentSchools(Array.isArray(res.data) ? [...res.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")) : [])).catch(() => {});
    }
  }, [loginRole, studentSchools.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      const res = await loginApi(email, password);
      login(res.data.user, res.data.token);
      setTimeout(() => { refreshFromBackend(); }, 500);
      toast.success("Welcome back!");
      navigate(res.data.user?.is_portal ? "/portal" : "/");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Invalid credentials");
      } else if (err.response) {
        toast.error(err.response.data?.detail || `Sign in failed (error ${err.response.status})`);
      } else {
        toast.error("Can't reach the server. Please check your connection or try again shortly.");
      }
    } finally { setLoading(false); }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    if (!studentSchoolId) { toast.error("Please select your school"); return; }
    if (!studentCode.trim()) { toast.error("Enter your access code"); return; }
    setLoading(true);
    try {
      const res = await api.post("/self-assessment/login", { access_code: studentCode.trim().toUpperCase(), school_id: studentSchoolId });
      toast.success(`Welcome, ${res.data.name}!`);
      navigate("/self-assess", { state: { code: studentCode.trim().toUpperCase(), studentInfo: res.data, schoolId: studentSchoolId } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid access code. Ask your teacher for help.");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await requestPasswordReset(email);
      toast.success("Your school's administrators have been notified to reset your password.");
      setMode("login");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Too many requests. Please wait a few minutes and try again.");
      } else {
        toast.error("Failed to request password reset");
      }
    } finally { setLoading(false); }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!setupName || !email || !password) { toast.error("Please fill in all fields"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const res = await register(setupName, email, password);
      login(res.data.user, res.data.token);
      setTimeout(() => { refreshFromBackend(); }, 500);
      toast.success("Admin account created! Welcome to SMARTIS.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally { setLoading(false); }
  };

  if (checkingSystem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#00305E]/5 via-white to-[#A62E1D]/5">
        <div className="text-[#00305E]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#00305E]/5 via-white to-[#A62E1D]/5 p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img src="/logo_full.png" alt="SMARTIS" className="mx-auto" style={{ maxWidth: '400px', width: '100%', height: 'auto' }} />
        </div>

        <Card className="border-0 shadow-xl">
          {/* Role Toggle - only show on login mode (not forgot/reset/setup) */}
          {mode === "login" && (
            <div className="flex border-b">
              <button
                type="button"
                onClick={() => setLoginRole("staff")}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  loginRole === "staff"
                    ? "text-[#00305E] border-b-2 border-[#00305E] bg-[#00305E]/5"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid="staff-login-tab"
              >
                Staff Login
              </button>
              <button
                type="button"
                onClick={() => setLoginRole("student")}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  loginRole === "student"
                    ? "text-[#00305E] border-b-2 border-[#00305E] bg-[#00305E]/5"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid="student-login-tab"
              >
                Student Self-Assessment
              </button>
            </div>
          )}

          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold" data-testid="login-title">
              {mode === "login"
                ? (loginRole === "staff" ? "Welcome back" : "Student Self-Assessment")
                : mode === "forgot" ? "Reset Password"
                : mode === "setup" ? "Initial Setup"
                : "Set New Password"}
            </CardTitle>
            <CardDescription>
              {mode === "login"
                ? (loginRole === "staff" ? "Sign in to access your dashboard" : "Enter your access code to rate yourself today")
                : mode === "forgot" ? "Enter your email and we'll notify your administrator to reset it"
                : mode === "setup" ? "Create your admin account to get started"
                : "Enter your new password"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* STAFF LOGIN */}
            {mode === "login" && loginRole === "staff" && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="you@school.edu" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="password-input" />
                </div>
                <Button type="submit" className="w-full h-11 bg-[#00305E] hover:bg-[#00305E]/90" disabled={loading} data-testid="login-btn">
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                <button type="button" onClick={() => setMode("forgot")} className="w-full text-sm text-[#00305E] hover:underline mt-2" data-testid="forgot-password-link">
                  Forgot your password?
                </button>
              </form>
            )}

            {/* STUDENT PIN LOGIN */}
            {mode === "login" && loginRole === "student" && (
              <form onSubmit={handleStudentLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="student-school">Your School</Label>
                  <select
                    id="student-school"
                    value={studentSchoolId}
                    onChange={(e) => setStudentSchoolId(e.target.value)}
                    className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00305E]"
                    data-testid="student-school-select"
                  >
                    <option value="">Select your school</option>
                    {studentSchools.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="student-code">Access Code</Label>
                  <Input
                    id="student-code"
                    type="text"
                    value={studentCode}
                    onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
                    placeholder="e.g., ABC123"
                    className="text-center text-2xl tracking-[0.3em] font-mono h-14 uppercase"
                    maxLength={6}
                    autoComplete="off"
                    data-testid="student-code-input"
                  />
                </div>
                <Button type="submit" className="w-full h-11 bg-[#00305E] hover:bg-[#00305E]/90" disabled={loading || !studentCode.trim() || !studentSchoolId} data-testid="student-login-btn">
                  {loading ? "Checking..." : "Start Assessment"}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Don't have a code? Ask your teacher.
                </p>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input id="reset-email" type="email" placeholder="you@school.edu" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="reset-email-input" />
                </div>
                <Button type="submit" className="w-full h-11 bg-[#00305E] hover:bg-[#00305E]/90" disabled={loading} data-testid="request-reset-btn">
                  {loading ? "Sending..." : "Request Password Reset"}
                </Button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-[#00305E] hover:underline mt-2" data-testid="back-to-login-link">
                  Back to Sign In
                </button>
              </form>
            )}

            {mode === "setup" && (
              <form onSubmit={handleSetup} className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-2">
                  No accounts found. Create your admin account to get started.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-name">Your Name</Label>
                  <Input id="setup-name" type="text" placeholder="e.g. Sarah Johnson" value={setupName} onChange={(e) => setSetupName(e.target.value)} className="h-11" data-testid="setup-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-email">Email</Label>
                  <Input id="setup-email" type="email" placeholder="you@school.edu" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="setup-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-password">Password</Label>
                  <Input id="setup-password" type="password" placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="setup-password-input" />
                </div>
                <Button type="submit" className="w-full h-11 bg-[#00305E] hover:bg-[#00305E]/90" disabled={loading} data-testid="setup-submit-btn">
                  {loading ? "Creating account..." : "Create Admin Account"}
                </Button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-[#00305E] hover:underline mt-2">
                  I already have an account
                </button>
              </form>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-sm mt-5">
          <Link to="/discover" data-testid="login-new-user-link" className="text-[#00305E] font-medium hover:underline">
            New user? Learn about SMARTIS Pro →
          </Link>
        </p>
        <p className="text-center text-[11px] leading-relaxed text-muted-foreground mt-4 max-w-sm mx-auto px-2">
          SMARTIS Pro is for Therapeutic, Independent, and Private Schools. If you weren&apos;t given a login by your
          administrator please contact them or email{" "}
          <a href="mailto:admin@smartispro.com" className="text-[#00305E] hover:underline">admin@smartispro.com</a>. If
          you are new to or interested in customizing SMARTIS Pro for your school{" "}
          <Link to="/discover" data-testid="login-click-here-link" className="text-[#00305E] hover:underline">Click Here</Link>.
        </p>
        <p className="text-center text-xs text-muted-foreground mt-4">
          <Link to="/privacy" data-testid="login-privacy-link" className="text-[#00305E] hover:underline">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
